import { ViewportState } from './ViewportState';
import { NavigationState } from './NavigationState';
import { computed, observable, ObservableMap } from 'mobx';
import { GoogleOptimizeClient } from '../modules/optimizeClient';
import queryString from 'querystring';

export const viewportState = new ViewportState();
window.addEventListener('resize', viewportState.update);

export const navigationState = new NavigationState();

/**
 * Specification of an experiment (e.g. feature toggles / AB tests)
 * We support two ways of controlling bucket assignment
 *  1. Through url parameters
 *  2. Through Google Optimize
 */
export type ExperimentSpec = {
    /**
     * An unique name that identifies experiment
     */
    name: string;
    /**
     * The url parameter name used for force-overriding the bucket assignment
     */
    forceOverrideKey: string;
    /**
     * The Google Optimize's experiment id corresponding to this test
     */
    optimizeExperimentId: string;
};

/**
 * Retrieves and caches the retrieved variant ids.
 */
class ExperimentState {
    @observable idToBucket: ObservableMap<string, Promise<string>> = observable.map();
    @observable resolvedBuckets: ObservableMap<string, string> = observable.map();

    async init(experimentSpecs: ExperimentSpec[]): Promise<void> {
        experimentSpecs.forEach((spec) => {
            if (spec.optimizeExperimentId) {
                this.idToBucket.set(spec.name, GoogleOptimizeClient.getVariantId(spec.optimizeExperimentId));
            }
        });
        if (location.search.length > 1) {
            const params = queryString.parse(location.search.slice(1));
            experimentSpecs.forEach((spec) => {
                if (spec.forceOverrideKey && params[spec.forceOverrideKey]) {
                    const value = params[spec.forceOverrideKey];
                    if (typeof value === 'string') {
                        this.idToBucket.set(spec.name, Promise.resolve(value));
                    }
                }
            });
        }
        await Promise.all(this.idToBucket.values());
        await Promise.all(
            experimentSpecs.map(async (spec) => {
                this.resolvedBuckets.set(spec.name, await this.idToBucket.get(spec.name));
            })
        );
    }

    getVariantId(name: string): Promise<string> {
        return this.idToBucket.get(name);
    }

    getResolvedBucketId(name: string): string | null {
        return this.resolvedBuckets.get(name);
    }

    @computed
    get groups() {
        return [...this.resolvedBuckets.entries()].map((e) => `${e[0]}:${e[1]}`).join(',');
    }

    hasVariantId(name: string, variantId: string) {
        return this.resolvedBuckets.get(name) === variantId;
    }
}

export const experimentState = new ExperimentState();
